/**
 * Two-clock ageing for the opener-descalce snapshot, mirroring
 * `live/venuePositions.ts` (`venueAgeMs`): the age at the moment the
 * snapshot LANDED is computed once, from the server clock
 * (`computedAt`) against the browser wall clock at receipt
 * (`receivedAt`) — mixing clocks here is fine because it is a ONE-TIME
 * snapshot taken at arrival, never re-evaluated. Everything ELAPSED SINCE
 * then is read off the MONOTONIC clock (`performance.now()`), never the
 * wall clock again, so a wall-clock correction AFTER the frame arrived
 * (forward or backward) cannot freeze — or worse, reverse — the displayed
 * age. A monotonic clock cannot be corrected.
 */
import { useEffect, useRef, useState } from 'react';

export interface DescalceSnapshotFrame {
  /** `computedAt` off the wire — the sweeper's own clock. */
  computedAt: number;
  /** `Date.now()` at the moment this frame was received by the FE. */
  receivedAt: number;
  /** `performance.now()` at the moment this frame was received by the FE. */
  receivedMono: number;
}

export function buildDescalceSnapshotFrame(
  computedAt: number,
  now: number = Date.now(),
  mono: number = performance.now()
): DescalceSnapshotFrame {
  return { computedAt, receivedAt: now, receivedMono: mono };
}

/**
 * Age of the snapshot, in ms. `ageAtFrame` is fixed at construction (it is
 * derived only from `frame`'s own fields, never re-read from the wall
 * clock), and `sinceFrame` advances purely off the monotonic delta passed
 * in — so calling this repeatedly as `mono` advances always grows the
 * result, regardless of what the wall clock does in between.
 */
export function descalceAgeMs(
  frame: DescalceSnapshotFrame,
  mono: number = performance.now()
): number {
  const ageAtFrame = Math.max(0, frame.receivedAt - frame.computedAt);
  const sinceFrame = Math.max(0, mono - frame.receivedMono);
  return ageAtFrame + sinceFrame;
}

export function isDescalceSnapshotStale(
  frame: DescalceSnapshotFrame,
  staleMs: number,
  mono: number = performance.now()
): boolean {
  return descalceAgeMs(frame, mono) > staleMs;
}

/** `setTimeout` truncates anything past this to "fire on the next tick". */
const MAX_TIMEOUT_MS = 2_147_483_647;
const STALE_TICK_GRACE_MS = 250;

/**
 * Rebuild the frame only when `computedAt` changes (a fresh snapshot
 * landed) — keeps the frame's identity (and its `receivedAt`/`receivedMono`
 * anchors) stable across the 5s poll re-renders that return the SAME
 * snapshot.
 */
export function useDescalceFreshnessFrame(
  computedAt: number | undefined
): DescalceSnapshotFrame | null {
  const frameRef = useRef<DescalceSnapshotFrame | null>(null);
  if (computedAt == null) return null;
  if (
    frameRef.current == null ||
    frameRef.current.computedAt !== computedAt
  ) {
    frameRef.current = buildDescalceSnapshotFrame(computedAt);
  }
  return frameRef.current;
}

/**
 * `true` once the frame has aged past `staleMs`. Re-renders the caller at
 * the stale boundary (a poll that keeps returning the SAME snapshot would
 * otherwise never re-evaluate staleness, since nothing else schedules a
 * render).
 */
export function useDescalceStale(
  frame: DescalceSnapshotFrame | null,
  staleMs: number | undefined
): boolean {
  const [tick, setTick] = useState(0);
  const horizon = staleMs != null && staleMs > 0 ? staleMs : null;

  useEffect(() => {
    if (!frame || horizon == null) return;
    const msLeft = horizon - descalceAgeMs(frame);
    if (msLeft <= 0) return;
    const delay = Math.min(msLeft + STALE_TICK_GRACE_MS, MAX_TIMEOUT_MS);
    const timer = setTimeout(() => setTick(n => n + 1), delay);
    return () => clearTimeout(timer);
  }, [frame, horizon, tick]);

  if (!frame || horizon == null) return false;
  return isDescalceSnapshotStale(frame, horizon);
}
